import DetailsPageLowerLayer from "../../../Components/DetailsPageLowerLayer/DetailsPageLowerLayer";
import Toolbar from "../../../CommonComponents/common/Toolbar";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import TopSection from "../../../CommonComponents/TopSection";
import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box } from "@mui/material";
import useRedirect from "../../../CustomHooks/useRedirect";
import CollapsibleHeader from "../../../CommonComponents/common/CollapsibleHeader";
import {
  fetchHeaderAndLinesData,
  fetchDeliveryData,
  fetchItemsData,
  fetchOrdersData,
} from "../../../services/InboundShipments/InboundShipmentsDetail";
import InboundShipmentHeader from "./InboundShipmentHeader";
import SectionSpinner from "../../../CommonComponents/common/Loader/SectionSpinner";
import { renderAmountCell } from "../../../utils/currencyUtils";
import { formatValue } from "../../../utils/currencyUtils";
import { parseDateValue } from "../../../utils/commonFunctions";

const normalizePoOrderType = (value = "") => {
  if (!value) return "";

  return decodeURIComponent(value)
    .replace(/[_-]/g, " ")                 // handle snake_case & kebab-case
    .replace(/([a-z])([A-Z])/g, "$1 $2")   // split camelCase
    .toLowerCase()
    .split(/\s+/)
    .map((word, index) => {
      if (word === "po") return "PO";
      if (index === 0) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join("");
};

const InboundShipmentDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hotItemsOnly, setHotItemsOnly] = useState(false);
  const [hotOrdersOnly, setHotOrdersOnly] = useState(false);
  const [headerData, setHeaderData] = useState({});
  const [linesRows, setLinesRows] = useState([]);
  const [deliveryRows, setDeliveryRows] = useState([]);
  const [itemsRows, setItemsRows] = useState([]);
  const [ordersRows, setOrdersRows] = useState([]);
  const [exceptionType, setExceptionType] = useState("");
  const redirect = useRedirect();


  // Separate loading states for each section
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingLines, setLoadingLines] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const breadcrumbArray = ['Supply', 'Inbound Shipments', 'Inbound Shipments Detail'];

  useEffect(() => {
    if (!id) return;

    const fetchHeaderAndOrders = async () => {
      let orderType; // default
      // --- Header & Lines ---
      setLoadingDetails(true);
      setLoadingLines(true);
      try {
        const headerDataResp = await fetchHeaderAndLinesData(id);
        setHeaderData(headerDataResp);
        setExceptionType(headerDataResp?.exceptionType);
        setLinesRows(
          headerDataResp?.line?.map(item => ({
            line_no: item.lineNo,
            ship_from: item?.shipFrom,
            from_site: item?.fromSite,
            ship_from_owner: item?.shipFromOwner,
            ship_to: item?.shipTo,
            to_site: item?.toSite,
            ship_to_owner: item?.shipToOwner,
            creation_date: item?.creationDate,
            container_ship_date: item?.containerShipDate,
            container_eta: item?.containerETA,
            container_pta: item?.containerPTA,
            container_type: item?.containerType,
            no_of_deliveries: item?.numberOfDeliveries,
            line_state: item?.lineState,
            line_supplier: item?.lineSupplier,
            supplier_description: item?.supplierDescription,
            line_customer: item?.lineCustomer,
            customer_description: item?.customerDescription,
            place_of_delivery: item?.placeOfDelivery,
            gross_wt: formatValue(item?.grossWeight),
            gross_wt_uom: item?.grossWeightUom,
            gross_volume: formatValue(item?.grossVolume),
            gross_volume_uom: item?.grossVolumeUom,
            line_exception: item?.lineException,
          })) ?? []
        );
        orderType = headerDataResp?.orderType;
      } catch (err) {
        console.error("Header & Lines API failed:", err);
      } finally {
        setLoadingDetails(false);
        setLoadingLines(false);
      }

      // --- Orders (always runs, even if header failed) ---
      setLoadingOrders(true);
      try {
        const ordersData = await fetchOrdersData(id, orderType);
        setOrdersRows(
          ordersData?.map(item => ({
            line_no: item?.lineNo,
            order_no: item?.orderNo,
            customer: item?.customer,
            customer_name: item?.customerName,
            created_by: item?.createdBy,
            supplier: item?.supplier,
            supplier_name: item?.supplierName,
            ship_to: item?.shipTo,
            ship_to_desc: item?.shipToDescription,
            // bill_to: item?.billTo,
            lob: item?.lob,
            inco_terms: item?.incoTerms,
            creation_date: item?.creationDate,
            amount: item?.amount,
            currency: item?.currency,
            header_state: item?.headerState,
            process_type: item?.processType,
            erp_order_type: item?.erpOrderType,
            payment_terms: item?.paymentTerms,
            order_type: item?.orderType,
            lmd: item?.lmd,
            order_exception: item?.orderException,
          })) ?? []
        );
      } catch (err) {
        console.error("Orders API failed:", err);
      } finally {
        setLoadingOrders(false);
      }
    }

    const fetchDeliveriesData = async () => {
      setLoadingDeliveries(true);
      try {
        const deliveriesData = await fetchDeliveryData(id);
        setDeliveryRows(
          deliveriesData?.map(item => ({
            line_no: item?.lineNo,
            delivery_no: item?.deliveryNo,
            delivery_type: "Inbound Delivery",
            lob: "CPS",
            supplier: item?.supplier,
            supplier_name: item?.supplierName,
            customer: item?.customer,
            customer_name: item?.customerName,
            ship_from: item?.shipFrom,
            ship_from_owner: item?.shipFromOwner,
            ship_from_site: item?.shipFromSite,
            ship_to: item?.shipTo,
            ship_to_owner: item?.shipToOwner,
            ship_to_site: item?.shipToSite,
            shipment_no: item?.shipmentNo,
            shipment_line_no: item?.shipmentLineNo,
            ship_mode: item?.shipMode,
            carrier: item?.carrier,
            planned_ship_date: item?.plannedShipDate,
            eta: item?.eta,
            delivery_exception: item?.deliveryException,
          })) ?? []
        );
      } catch (err) {
        console.error("Delivery API failed:", err);
        setDeliveryRows([]);
      } finally {
        setLoadingDeliveries(false);
      }
    };

    const fetchItemData = async () => {
      setLoadingItems(true);
      try {
        const itemsData = await fetchItemsData(id);
        setItemsRows(
          itemsData?.map(item => ({
            line_no: item?.lineNo,
            item_name: item?.itemName,
            ship_to: item?.shipTo,
            item: item?.item,
            shipped_quantity: formatValue(item?.shippedQuantity),
          })) ?? []
        );
      } catch (err) {
        console.error("Items API failed:", err);
        setItemsRows([]);
      } finally {
        setLoadingItems(false);
      }
    };
    // Run header/orders first, deliveries & items independently in parallel
    fetchHeaderAndOrders();
    fetchDeliveriesData();
    fetchItemData();
  }, [id]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/core/supply/shipment");
    }
  };

  const redirectFn = useMemo(() => {
    return (path, state) => {
      redirect(path, state);
    };
  }, [redirect]);

  const toolbarProps = {
    number: id,
    status: exceptionType,
    icon: <LocalShippingIcon />,
    onBack: handleBack,
    isCollapsed,
    toggleHeader: () => setIsCollapsed((prev) => !prev),
  };

  const linesTab = useMemo(() => ({
    label: `Lines(${linesRows?.length})`,
    loading: loadingLines,
    columns: [
      { field: "line_no", headerName: "Line No" },
      { field: "ship_from", headerName: "Ship From" },
      { field: "from_site", headerName: "From(Site)" },
      { field: "ship_from_owner", headerName: "Ship From Owner" },
      { field: "ship_to", headerName: "Ship To" },
      { field: "to_site", headerName: "To(Site)" },
      { field: "ship_to_owner", headerName: "Ship To Owner" },
      { field: "creation_date", headerName: "Creation Date",
              comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB), },
      { field: "container_ship_date", headerName: "Container Ship Date",
              comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB), },
      { field: "container_eta", headerName: "Container ETA",
              comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB), },
      { field: "container_pta", headerName: "Container PTA",
              comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB), },
      // { field: "container_ata", headerName: "Container ATA" },
      { field: "container_type", headerName: "Container Type" },
      //{ field: "no_of_deliveries", headerName: "No of Deliveries" },
      { field: "line_state", headerName: "Line State" },
      // { field: "temp_controlled", headerName: "Temp Controlled" },
      { field: "line_supplier", headerName: "Line Supplier" },
      { field: "supplier_description", headerName: "Supplier Description" },
      { field: "line_customer", headerName: "Line Customer" },
      { field: "customer_description", headerName: "Customer Description" },
      // { field: "freight_category", headerName: "Freight Category" },
      { field: "place_of_delivery", headerName: "Place of Delivery" },
      { field: "gross_wt", headerName: "Gross Wt" },
      { field: "gross_wt_uom", headerName: "Gross Wt UOM" },
      { field: "gross_volume", headerName: "Gross Volume" },
      { field: "gross_volume_uom", headerName: "Gross Volume UOM" },
      // { field: "load_id", headerName: "Load Id" },
    ],
    rows: linesRows
  }), [linesRows, loadingLines]);

  const ordersTab = useMemo(
    () => ({
      label: `Orders(${ordersRows.length})`,
      loading: loadingOrders,
      columns: [
        {
          field: "order_no",
          headerName: "Order No",
          cellRenderer: (params) => {
            const { value, data } = params;
            const orderType = normalizePoOrderType(data?.order_type);
            return (
              <span
                onClick={() =>
                  redirectFn(`/core/supply/order/order-detail/${orderType}/${value}`, {
                    menu: "Supply",
                    label: "Purchase Orders",
                    outerLabel: "Purchase Orders Detail",
                    previousPage: window.location.pathname,
                  })
                }
                style={{
                  color: "#1976d2",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {value}
              </span>
            );
          },
        },
        { field: "customer", headerName: "Customer" },
        { field: "customer_name", headerName: "Customer Name" },
        { field: "created_by", headerName: "Created By" },
        { field: "supplier", headerName: "Supplier" },
        { field: "supplier_name", headerName: "Supplier Name" },
        { field: "ship_to", headerName: "Ship To" },
        {
          field: "ship_to_desc",
          headerName: "Ship To Description",
        },
        { field: "bill_to", headerName: "Bill To" },
        { field: "lob", headerName: "LOB" },
        { field: "inco_terms", headerName: "Inco Terms" },
        { field: "creation_date", headerName: "Creation Date" ,
                comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB)},
        { field: "amount", headerName: "Amount", cellRenderer: renderAmountCell },
        { field: "currency", headerName: "Currency" },
        // { field: "data_exceptions", headerName: "Data Exceptions" },
        { field: "header_state", headerName: "Header State" },
        // { field: "notes", headerName: "Notes" },
        { field: "process_type", headerName: "Process Type" },
        { field: "erp_order_type", headerName: "ERP Order Type" },
        { field: "payment_terms", headerName: "Payment Terms" },
        { field: "order_type", headerName: "Order Type" },
        { field: "lmd", headerName: "Last Modified Date",
                comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
      ],
      rows: ordersRows,
      // toggle: {
      //   label: "Show Only Hot Orders",
      //   checked: hotOrdersOnly,
      //   onChange: () => setHotOrdersOnly(!hotOrdersOnly),
      // }
    }),
    [ordersRows, loadingOrders, redirectFn]
  );

  const deliveriesTab = useMemo(
    () => ({
      label: `Deliveries(${deliveryRows.length})`,
      loading: loadingDeliveries,
      columns: [
        {
          field: "delivery_no",
          headerName: "Delivery No",
          cellRenderer: (params) => {
            const { value } = params;
            return (
              <div
                style={{
                  borderLeft: "5px solid black",
                  paddingLeft: 8,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span
                  onClick={() =>
                    redirect(`/core/supply/delivery/delivery-detail/${value}`, {
                      menu: "Supply",
                      label: "Inbound Deliveries",
                      outerLabel: "Inbound Deliveries Detail",
                      previousPage: window.location.pathname,
                    })
                  }
                  style={{
                    color: "#1976d2",
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                >
                  {value}
                </span>
              </div>
            );
          },
        },
        { field: "delivery_type", headerName: "Delivery Type" },
        { field: "lob", headerName: "LOB" },
        { field: "supplier", headerName: "Supplier" },
        { field: "supplier_name", headerName: "Supplier Name" },
        { field: "customer", headerName: "Customer" },
        { field: "customer_name", headerName: "Customer Name" },
        { field: "ship_from", headerName: "Ship From" },
        { field: "ship_from_owner", headerName: "Ship From Owner" },
        { field: "ship_from_site", headerName: "Ship From (Site)" },
        { field: "ship_to", headerName: "Ship To" },
        { field: "ship_to_owner", headerName: "Ship To Owner" },
        { field: "ship_to_site", headerName: "Ship To (Site)" },
        { field: "shipment_no", headerName: "Shipment No" },
        { field: "shipment_line_no", headerName: "Shipment Line No" },
        { field: "ship_mode", headerName: "Ship Mode" },
        // { field: "bol_no", headerName: "BOL No" },
        { field: "carrier", headerName: "Carrier" },
        { field: "planned_ship_date", headerName: "Planned Ship Date",
                comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
        // { field: "actual_ship_date", headerName: "Actual Ship Date" },
        { field: "eta", headerName: "ETA",
                comparator: (vA, vB) => parseDateValue(vA) - parseDateValue(vB) },
        // { field: "ata", headerName: "ATA" },
        // { field: "truck_no", headerName: "Truck No" },
        // { field: "trailer_no", headerName: "Trailer No" },
      ],
      rows: deliveryRows
    }), [deliveryRows, loadingDeliveries, redirect]);

  const itemsTab = useMemo(
    () => ({
      label: `Items(${itemsRows.length})`,
      loading: loadingItems,
      columns: [
        { field: "item_name", headerName: "Material Description", flex: 1 },
        { field: "ship_to", headerName: "Ship To", flex: 1 },
        {
          field: "item",
          headerName: "Material Number",
          cellRenderer: (params) => {
            const { value, data } = params;
            const redirect = params.colDef.cellRendererParams?.redirect;
            const siteCode = data?.to ?? " ";
            return (
              <span
                onClick={() =>
                  redirect(
                    `/core/inventory/node/inventory/inventory-detail/${value}/${siteCode}`,
                    {
                      menu: "Inventory",
                      label: "Inventory List",
                      outerLabel: "Inventory Detail",
                      previousPage: window.location.pathname,
                    }
                  )
                }
                style={{
                  color: "#1976d2",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {value}
              </span>
            );
          },
          cellRendererParams: {
            redirect: redirectFn,
          },
        },
        {
          field: "shipped_quantity",
          headerName: "Shipped Quantity",
          flex: 1
        },
      ],
      rows: itemsRows,
      toggle: {
        label: "Show only Hot Items",
        checked: hotItemsOnly,
        onChange: () => setHotItemsOnly(!hotItemsOnly),
      },
    }),
    [itemsRows, hotItemsOnly, loadingItems, redirectFn]
  );

  const tabs = [linesTab, ordersTab, deliveriesTab, itemsTab];

  return (
    <div>
      <TopSection
        breadcrumbArray={breadcrumbArray}
        hideHeader={false}
        headerName=""
        hideTitle={true}
      />
      <Toolbar {...toolbarProps} />
      <CollapsibleHeader
        upperLayer={
          <Box sx={{ position: "relative" }}>
            {loadingDetails && <SectionSpinner />}
            <InboundShipmentHeader hProps={headerData} linesData={linesRows} />
          </Box>
        }
        lowerLayer={<DetailsPageLowerLayer tabs={tabs} />}
        isCollapsed={isCollapsed}
      />
    </div>
  );
};

export default InboundShipmentDetail;